/* eslint-disable no-underscore-dangle */
import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  getEntityType,
} from 'web-utils';

import {
  Spinner,
  NotFound,
  NestedWorkout,
  WorkoutHeader,
  WorkoutExercisesList,
} from 'web-components';

import {
  Flex,
  WorkoutExercisesView,
} from 'web-styled';

type Props = {
  id: number,
  parentId: number,
  resourceType: string,
};

const AdditionalWorkout = ({
  id,
  parentId,
  resourceType,
}: Props) => {
  const parentType = 'personalWorkouts';
  const mapState = useCallback(state => ({
    status: getStatus(
      state,
      [
        `${resourceType}.requests.getFullAdditionalWorkout.status`,
        `${resourceType}.meta[${id}].readStatus`,
      ],
      true,
    ),
    options: coreSelectors.getEntitySearchOptions(
      resourceType,
      'library',
    )(state),
  }), [id]);

  const { status, options } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();
  useEffect(() => {
    if (id) {
      dispatch(
        coreActions.requestFullWorkout({
          id,
          resourceType: parentType,
          addtionalType: resourceType,
        }),
      );
    }
  }, [id]);

  const type = getEntityType(resourceType);

  const handleAdd = (payload, callbacks) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: parentId,
        payload,
        resourceType: parentType,
        callbacks,
      }),
    );
  };

  return id ? (
    <Flex fillSpace padding="20px 0">
      {status.succeeded && (
        <Flex column spacing={20}>
          <Flex margin="-10px">
            <WorkoutHeader
              readOnly
              workoutId={id}
              parentType={parentType}
              resourceType={resourceType}
            />
          </Flex>
          <WorkoutExercisesView>
            <WorkoutExercisesList
              readOnly
              type={type}
              workoutId={id}
            />
          </WorkoutExercisesView>
        </Flex>
      )}
      {status.pending && (
        <Spinner message="Workout..." />
      )}
      {status.failed && (
        <NotFound
          title="Not Found"
        />
      )}
    </Flex>
  ) : (
    <NestedWorkout
      type={type}
      readOnly
      options={options}
      onRequestAdd={({ value }) => {
        handleAdd(type === 'warmup'
          ? { warmup_id: value }
          : { cooldown_id: value });
      }}
      onRequestAddNew={({ value }) => {
        const payload = type === 'warmup'
          ? { warmup_id: '0' }
          : { cooldown_id: '0' };

        const callbacks = {
          onSuccess: (result) => {
            dispatch(
              coreActions.requestUpdateEntity({
                id: type === 'warmup' ? result.warmup_id : result.cooldown_id,
                payload: { name: value },
                resourceType,
              }),
            );
          },
        };

        handleAdd(payload, callbacks);
      }}
    />
  );
};

export default AdditionalWorkout;
